import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Row, Col, Card, CardBody, Button } from 'reactstrap';
import { MDBDataTable } from 'mdbreact';
import Select from 'react-select';

import { activateAuthLayout } from '../../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/react-hooks';

import { formatPrice } from '../../../../helpers/common';
import SettingsSwitch from '../../../../components/SettingsSwitch';

const PRODUCTS = gql`
  query getProducts {
    Products {
      ProductId
      ProductCode
      Name
      CatId
      ProductCategory {
        Name
      }
      Active
      SyncWithXero
      XeroTrackedAsInventory
      XeroInventoryAssetAccountCode
      XeroCOA
      XeroCOASales
      ProductPriceLines {
        PriceLineId
        PriceType
        CostPrice
        Multiplier
        OpexProration
        ProductPrices {
          QuoteTypeId
          Price
          ShowPrice
        }
      }
    }
  }
`;

const GET_PRODUCT_CATEGORIES = gql`
  query {
    ProductCategories {
      CatId
      Name
    }
  }
`;

const ProductList = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const location = useLocation();
  const history = useHistory();

  // initialize states
  const currentQuoteParam =
    location.search && location.search.includes('?currentQuote=')
      ? `?currentQuote=${location.search.split('=').pop()}`
      : '';
  const [productsData, setProductsData] = useState({
    columns: [
      {
        label: [
          'Product Code',
          <i key={`product-id`} className="fa fa-sort ml-2"></i>,
        ],
        field: 'ProductCode',
        sort: 'asc',
      },
      {
        label: [
          'Product Name',
          <i key={`product-name`} className="fa fa-sort ml-2"></i>,
        ],
        field: 'Name',
        sort: 'asc',
      },
      {
        label: [
          'Category',
          <i key={`category-id`} className="fa fa-sort ml-2"></i>,
        ],
        field: 'Category',
        sort: 'asc',
      },
      {
        label: [
          'Active',
          <i key={`active`} className="fa fa-sort ml-2"></i>,
        ],
        field: 'Active',
        sort: 'asc',
      },
      {
        label: [
          'Price Type',
          <i key={`price-type`} className="fa fa-sort ml-2"></i>,
        ],
        field: 'PriceType',
        sort: 'asc',
      },
      {
        label: [
          'Cost Price',
          <i key="cost-price" className="fa fa-sort ml-2"></i>,
        ],
        field: 'CostPrice',
        sort: 'asc',
      },
      {
        label: [
          'Multiplier',
          <i key="multiplier" className="fa fa-sort ml-2"></i>,
        ],
        field: 'Multiplier',
        sort: 'asc',
      },
      {
        label: [
          'CAPEX Price',
          <i key="capex-price" className="fa fa-sort ml-2"></i>,
        ],
        field: 'CapexPrice',
        sort: 'asc',
      },
      {
        label: [
          'OPEX Proration',
          <i key={`xero-linked`} className="fa fa-sort ml-2"></i>,
        ],
        field: 'OpexProration',
        sort: 'asc',
      },
      {
        label: 'Action',
        field: 'Action',
        sort: 'disabled',
      },
    ],
    rows: [],
  });
  const [selectedProductFilter, setSelectedProductFilter] = useState(null);

  // initialize apollo graphql queries and mutations
  const { data } = useQuery(PRODUCTS);
  const { data: ProductCategories } = useQuery(GET_PRODUCT_CATEGORIES);
  const sortColumnRef = useRef('ProductCode');
  const sortDirectionRef = useRef('asc');
  const inActiveProductRef = useRef(false);
  const categoryFilter = useRef(null);

  const reRenderRows = useCallback((products) => {
    products = products.sort(function (a, b) {
      let comparison = 0;

      if (a[sortColumnRef.current] === null) return 1;
      if (b[sortColumnRef.current] === null) return -1;

      if (a[sortColumnRef.current] < b[sortColumnRef.current]) comparison = -1;
      if (a[sortColumnRef.current] > b[sortColumnRef.current]) comparison = 1;

      return comparison === 0 ? 0 : (sortDirectionRef.current === 'desc' ? comparison * -1 : comparison);
    });

    return products;
  }, []);

  const editProduct = useCallback((e, productId) => {
    e.preventDefault();
    history.push(`/admin/product/edit/${productId}${currentQuoteParam}`);
  }, [history, currentQuoteParam]);

  const processProducts = useCallback((products_arr) => {
    return products_arr
      .map((prod, index) => {
        const lines = prod.ProductPriceLines ?? [];
        const priceLine = lines.length === 1 ? lines[0] : null;

        let prices = {
          cost_price: priceLine?.CostPrice ?? null,
          multiplier: priceLine?.Multiplier ?? null,
          capex_price: null,
        };

        if (priceLine) {
          (priceLine.ProductPrices ?? []).forEach((price) => {
            if (price.QuoteTypeId === 1) prices.capex_price = price.Price;
          });
        }

        return {
          IsActive: prod.Active ?? 0,
          CatId: prod.CatId ?? '',
          ProductId: prod.ProductId ?? '',
          ProductCode: prod.ProductCode ?? '',
          Name: prod.Name ?? '',
          Category: prod.ProductCategory?.Name ?? '--',
          PriceType: (() => {
            const val = priceLine?.PriceType ?? (lines.length ? 'mixed' : '--');
            return <span className="text-capitalize" searchvalue={val}>{val}</span>;
          })(),
          OpexProration: priceLine?.PriceType === 'hardware'
            ? (priceLine.OpexProration ? 'Yes' : 'No')
            : '--',
          Active: prod.Active ? 'Yes' : 'No',
          CostPrice: <span searchvalue={prices.cost_price ?? -Infinity}>{priceLine && prices.cost_price != null ? formatPrice(prices.cost_price) : '--'}</span>,
          Multiplier: <span searchvalue={prices.multiplier ?? -Infinity}>{priceLine?.PriceType === 'hardware' && prices.multiplier != null ? prices.multiplier : '--'}</span>,
          CapexPrice: <span searchvalue={prices.capex_price ?? -Infinity}>{priceLine && prices.capex_price != null ? formatPrice(prices.capex_price) : '--'}</span>,
          Action: (
            <Button
              color="info"
              size="sm"
              onClick={(e) => editProduct(e, prod.ProductId)}
            >
              Edit
            </Button>
          ),
        };
      });
  }, [editProduct]);

  const applyFilters = useCallback((rawProducts) => {
    let products = categoryFilter.current
      ? rawProducts.filter(prod => prod.CatId === categoryFilter.current)
      : rawProducts;
    products = processProducts(products);
    if (!inActiveProductRef.current) {
      products = products.filter(prod => prod.IsActive === 1);
    }
    return reRenderRows(products);
  }, [processProducts, reRenderRows]);

  const initializedRef = useRef(false);

  const createNewProduct = (e) => {
    e.preventDefault();
    history.push(`/admin/product/create${currentQuoteParam}`);
  };

  const toggleProduct = (e) => {
    inActiveProductRef.current = !inActiveProductRef.current;
    const products = applyFilters(data.Products);
    setProductsData(prev => ({ ...prev, rows: products, products }));
  };

  const categorySort = ({ column, direction }) => {
    sortColumnRef.current = column;
    sortDirectionRef.current = direction;
  };

  useEffect(() => {
    if (data && !initializedRef.current) {
      initializedRef.current = true;
      const products = applyFilters(data.Products);
      setProductsData(prev => ({ ...prev, rows: products, products }));
    }
  }, [data, applyFilters]);

  return (
    <React.Fragment>
      {
        <div className="content">
          <div className="container-fluid">
            <div className="page-title-box">
              <Row className="align-items-center">
                <Col sm="6">
                  <h4 className="page-title">Products</h4>
                </Col>
                <Col sm="6" className="text-right">
                  <Button color="success" onClick={createNewProduct}>
                    Create New Product
                  </Button>
                </Col>
              </Row>
            </div>

            <Row>
              <Col lg="12">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center w-100">
                    <SettingsSwitch
                      checked={inActiveProductRef.current}
                      onChange={(e) => toggleProduct(e)}
                      name="show_inactive_products"
                    />
                    <span className="ml-2">
                      Show inactive products
                    </span>
                  </div>

                  <Select
                    className="w-50"
                    isClearable={true}
                    options={ProductCategories ? [
                      ...ProductCategories.ProductCategories.map((data) => {
                        return {
                          value: data.CatId,
                          label: data.Name,
                        };
                      }),
                    ] : []}
                    placeholder={'Filter by category...'}
                    value={selectedProductFilter}
                    onChange={(e) => {
                      categoryFilter.current = e ? e.value : null;
                      const products = applyFilters(data.Products);
                      setProductsData(prev => ({ ...prev, rows: products, products }));
                      setSelectedProductFilter(e);
                    }}
                  />
                </div>
                <Card>
                  {!data ? (
                    <div className="spinner-border mx-auto my-5" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : (
                    <CardBody style={{ zIndex: 0 }}>
                      <MDBDataTable
                        sortable
                        striped
                        borderless
                        theadColor="table-dark"
                        hover
                        responsive
                        noBottomColumns
                        noRecordsFoundLabel={'No products found'}
                        order={['ProductCode', 'asc']}
                        data={productsData}
                        sortRows={['Category', 'PriceType', 'CostPrice', 'Multiplier', 'CapexPrice']}
                        onSort={(e) => categorySort(e)}
                        className="products-list-table"
                      />
                    </CardBody>
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      }
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(ProductList);
